
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { signInSchema, signUpSchema, type SignInData, type SignUpData } from "@/lib/validations";
import { useRecaptcha } from "./useRecaptcha";
import { useEffect } from "react";

export function useAuthForms() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const { executeRecaptcha, isLoaded } = useRecaptcha();

  // Carrega o reCAPTCHA apenas quando necessário
  useEffect(() => {
    if (isLoaded) {
      console.log('reCAPTCHA carregado para autenticação');
    }
  }, [isLoaded]);

  const verifyRecaptcha = async (token: string, action: string): Promise<{ success: boolean; score?: number }> => {
    try {
      const { data, error } = await supabase.functions.invoke('verify-recaptcha', {
        body: { token, action }
      });

      if (error) {
        console.error('Erro ao chamar função verify-recaptcha:', error);
        return { success: false };
      }

      if (!data || !data.success) {
        console.error('Verificação reCAPTCHA falhou:', data);
        return { success: false };
      }

      return { success: true, score: data.score };
    } catch (error) {
      console.error('Erro na verificação reCAPTCHA:', error);
      return { success: false };
    }
  };

  const handleSignIn = async (email: string, password: string) => {
    setLoading(true);

    try {
      // Executar reCAPTCHA — obrigatório
      if (!isLoaded) {
        toast({
          title: "Erro de segurança",
          description: "O sistema de verificação ainda está carregando. Tente novamente em alguns segundos.",
          variant: "destructive",
        });
        return;
      }

      const recaptchaToken = await executeRecaptcha('login');
      if (!recaptchaToken) {
        toast({
          title: "Erro de segurança",
          description: "Não foi possível verificar que você não é um robô. Recarregue a página e tente novamente.",
          variant: "destructive",
        });
        return;
      }

      // Verificar reCAPTCHA no servidor
      const recaptchaResult = await verifyRecaptcha(recaptchaToken, 'login');
      if (!recaptchaResult.success) {
        toast({
          title: "Verificação de segurança falhou",
          description: "Atividade suspeita detectada. Tente novamente mais tarde.",
          variant: "destructive",
        });
        return;
      }

      // Validate input data
      const validatedData = signInSchema.parse({ email, password });
      
      const { data, error } = await supabase.auth.signInWithPassword({
        email: validatedData.email,
        password: validatedData.password,
      });

      if (error) throw error;
      
      toast({
        title: "Login realizado com sucesso!",
        description: "Bem-vindo de volta ao K-recão Lanches.",
      });
      
      navigate("/");
    } catch (error: any) {
      let errorMessage = "Erro desconhecido";
      
      if (error.errors && Array.isArray(error.errors)) {
        // Zod validation errors
        errorMessage = error.errors.map((err: any) => err.message).join(", ");
      } else if (error.message) {
        // Supabase errors
        errorMessage = error.message;
      }
      
      toast({
        title: "Erro no login",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSignUp = async (email: string, password: string, name: string, phone: string) => {
    setLoading(true);

    try {
      // Executar reCAPTCHA — obrigatório
      if (!isLoaded) {
        toast({
          title: "Erro de segurança",
          description: "O sistema de verificação ainda está carregando. Tente novamente em alguns segundos.",
          variant: "destructive",
        });
        return false;
      }

      const recaptchaToken = await executeRecaptcha('signup');
      if (!recaptchaToken) {
        toast({
          title: "Erro de segurança",
          description: "Não foi possível verificar que você não é um robô. Recarregue a página e tente novamente.",
          variant: "destructive",
        });
        return false;
      }

      // Verificar reCAPTCHA no servidor
      const recaptchaResult = await verifyRecaptcha(recaptchaToken, 'signup');
      if (!recaptchaResult.success) {
        toast({
          title: "Verificação de segurança falhou",
          description: "Atividade suspeita detectada. Tente novamente mais tarde.",
          variant: "destructive",
        });
        return false;
      }

      // Validate input data
      const validatedData = signUpSchema.parse({ email, password, name, phone });
      
      const { data, error } = await supabase.auth.signUp({
        email: validatedData.email,
        password: validatedData.password,
        options: {
          data: {
            name: validatedData.name,
            phone: validatedData.phone
          },
          emailRedirectTo: "https://krecaolanches.vercel.app/"
        },
      });

      if (error) throw error;
      
      toast({
        title: "Conta criada com sucesso!",
        description: "Verifique seu email para confirmar a conta.",
      });
      
      return true;
    } catch (error: any) {
      let errorMessage = "Erro desconhecido";
      
      if (error.errors && Array.isArray(error.errors)) {
        // Zod validation errors
        errorMessage = error.errors.map((err: any) => err.message).join(", ");
      } else if (error.message) {
        // Supabase errors
        errorMessage = error.message;
      }
      
      toast({
        title: "Erro no cadastro",
        description: errorMessage,
        variant: "destructive",
      });
      return false;
    } finally {
      setLoading(false);
    }
  };

  return {
    loading,
    handleSignIn,
    handleSignUp
  };
}
